import { Toaster } from '@/components/ui/toaster';
import { useNotificationsDB, InAppNotification } from '@/hooks/useNotificationsDB';
import React, { createContext, ReactNode, useContext } from 'react';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  created_at: string;
  category: 'appointment' | 'payment' | 'service' | 'general' | 'promotion';
  action_url?: string;
  action_text?: string;
}

function toNotification(n: InAppNotification): Notification {
  const meta = n.metadata || {};
  return {
    id: n.id,
    title: n.title,
    message: n.message,
    type: n.type,
    read: n.read,
    created_at: n.created_at,
    category: (meta.category as Notification['category']) || 'general',
    action_url: meta.action_url as string | undefined,
    action_text: meta.action_text as string | undefined,
  };
}

interface NotificationContextType {
  notifications: Notification[];
  stats: { total: number; unread: number };
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

// Notificações reais, lidas de `in_app_notifications` (RLS: só as do próprio
// utilizador) via useNotificationsDB, com subscrição em tempo real do
// Supabase. Antes deste ficheiro gerava notificações ALEATÓRIAS FALSAS a
// cada 30s ("para demonstração") — removido: era enganoso em produção.
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { inAppNotifications, unreadCount, markAsRead, markAllAsRead, deleteInAppNotification } = useNotificationsDB();

  const contextValue: NotificationContextType = {
    notifications: inAppNotifications.map(toNotification),
    stats: { total: inAppNotifications.length, unread: unreadCount },
    markAsRead,
    markAllAsRead,
    removeNotification: deleteInAppNotification,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Toaster />
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};
